"use client";

import React, { useState } from "react";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import menuData from "../../data/header.json";
import NavProps from "./NavProps";
import menuImg from "../../public/images/menu-img/menu-img-2.png";

/**
 * Componente Nav que renderiza el menú principal de navegación.
 * Filtra elementos según la autenticación y muestra menús desplegables o megamenús.
 *
 * @returns {JSX.Element} - Renderizado del menú principal.
 */
const Nav = () => {
  // Obtener la sesión actual y la ruta actual
  const { data: session } = useSession();
  const pathname = usePathname();

  // Estado para controlar la visibilidad de secciones desplegables (por ejemplo, "Tools", "Pages")
  const [sectionStates, setSectionStates] = useState({
    Tools: true,
    Pages: true,
  });

  /**
   * Alterna la visibilidad de una sección del menú.
   *
   * @param {string} sectionTitle - Título de la sección a alternar.
   */
  const toggleSection = (sectionTitle) => {
    setSectionStates((prevState) => ({
      ...prevState,
      [sectionTitle]: !prevState[sectionTitle],
    }));
  };

  /**
   * Verifica si un enlace debe considerarse activo.
   * Para "/" se realiza una comparación exacta, para otros enlaces se comprueba
   * si la ruta actual comienza con alguno de los enlaces activos.
   *
   * @param {string} href - Enlace a evaluar.
   * @param {string[]} activeLinks - Lista de enlaces que se consideran activos.
   * @returns {boolean} - Verdadero si el enlace es activo.
   */
  const isActive = (href, activeLinks = []) => {
    if (href === "/") {
      return pathname === "/";
    }
    return activeLinks.some((link) => pathname.startsWith(link));
  };

  return (
    <ul className="mainmenu">
      {menuData &&
        menuData.nav
          .filter((menuItem) => {
            const isAuthed = !!session;
            const v = menuItem.visibility ?? "public";

            if (v === "public") return true;
            if (v === "auth") return isAuthed;
            if (v === "guest") return !isAuthed;

            return true;
          })
          .map((menuItem, index) => (
            <li
              key={index}
              className={`${menuItem.dropdown ? "has-dropdown has-menu-child-item position-relative" : ""} ${
                menuItem.megamenu ? "with-megamenu has-menu-child-item" : ""
              }`}
            >
              {menuItem.link === "#" ? (
                // Enlace no navegable que permite alternar la visibilidad de la sección
                <a
                  href="#"
                  className={`${!sectionStates[menuItem.text] ? "open" : ""}`}
                  onClick={() => toggleSection(menuItem.text)}
                >
                  {menuItem.text}
                  {menuItem.isIcon && (
                    <i className="fa-regular fa-chevron-down"></i>
                  )}
                </a>
              ) : (
                // Enlace navegable
                <Link
                  href={menuItem.link}
                  className={
                    isActive(menuItem.link, menuItem.activeLinks)
                      ? "active"
                      : ""
                  }
                >
                  {menuItem.text}
                  {menuItem.isIcon && (
                    <i className="fa-regular fa-chevron-down"></i>
                  )}
                </Link>
              )}

              {menuItem.isMenu &&
              !menuItem.inner &&
              !menuItem.dashboard &&
              !menuItem.upcoming ? (
                // Menú desplegable simple
                <ul
                  className={`submenu ${!sectionStates[menuItem.text] ? "d-block" : ""}`}
                >
                  {menuItem.subItem &&
                    menuItem.subItem.map((subItem, subIndex) => (
                      <li key={subIndex}>
                        <Link
                          href={!subItem.isDisable ? subItem.link : "#"}
                          className={`${isActive(subItem.link) ? "active" : ""} ${
                            subItem.isDisable ? "disabled" : ""
                          }`}
                        >
                          <span>{subItem.title}</span>
                          {subItem.badge && (
                            <div className="rainbow-badge-card badge-sm ml--5">
                              {subItem.badge}
                            </div>
                          )}
                        </Link>
                      </li>
                    ))}
                </ul>
              ) : menuItem.isMenu ? (
                // Megamenú que puede incluir varias secciones (inner, dashboard, upcoming)
                <div
                  className={`rainbow-megamenu ${!sectionStates[menuItem.text] ? "d-block active" : ""}`}
                >
                  <div className="wrapper">
                    <div className="row row--0">
                      <NavProps list={menuItem.inner} />
                      <NavProps list={menuItem.dashboard} />
                      <NavProps list={menuItem.upcoming} />
                      <div className="col-lg-3 single-mega-item">
                        <div className="header-menu-img">
                          <Image
                            src={menuImg}
                            width={326}
                            height={458}
                            alt="Menu Split Image"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                ""
              )}
            </li>
          ))}
    </ul>
  );
};

export default Nav;
